import { AdminLinkButton } from "@/admin/AdminLinkButton.tsx";
import { Extra, Missing } from "@/admin/AdminTable.tsx";
import { gql } from "@/admin/graphql.gen/gql.ts";
import type { BillingAccount } from "@/admin/graphql.gen/graphql.ts";
import { adminBillingAccountLink } from "@/admin/itemLinks.ts";
import { useLogError } from "@/modules/error/useLogError.ts";
import { useQuery } from "@apollo/client/react";

interface BillingAccountBlockProps {
    value: Pick<BillingAccount, "title" | "email" | "id" | "company_name"> | null | undefined;
    showId?: boolean;
    showCompanyName?: boolean;
    isLink?: boolean;
}

export function BillingAccountBlock({
    value,
    showId = false,
    showCompanyName = false,
    isLink = true,
}: BillingAccountBlockProps) {
    if (value == null) {
        return <Missing />;
    }
    const { title, email, id, company_name } = value;

    const label = (
        <>
            {showId && <>[{id}] </>}
            {title}
            {showCompanyName && company_name && <> &bull; {company_name}</>}
        </>
    );

    const content = (
        <Extra className="max-w-300px text-ellipsis overflow-x-hidden" value={label}>
            {email}
        </Extra>
    );
    return isLink ? <AdminLinkButton action={adminBillingAccountLink(id)} label={content} /> : content;
}

BillingAccountBlock.fragment = gql(/* GraphQL */ `
    fragment BillingAccountBlock on BillingAccount {
        title
        email
        id
    }
`);

//#region Non-GraphQL views integration (allow querying by single ID)

const BA_ID_QUERY = gql(/* GraphQL */ `
    query GetBillingAccountById($id: String!) {
        billing_account_details(id: $id) {
            ...BillingAccountBlock
        }
    }
`);

export function BillingAccountById({ value }: { value: BillingAccount["id"] | number }) {
    const id = String(value);

    const { data, error } = useQuery(BA_ID_QUERY, { variables: { id } });
    // TODO: Centralized logging of Apollo errors?
    useLogError(error, "Error loading billing account");

    return data ? (
        <BillingAccountBlock showId value={data.billing_account_details} />
    ) : (
        // Dummy fallback while loading
        <BillingAccountBlock showId value={{ title: id, email: error?.message ?? "...", id }} />
    );
}

//#endregion
